'use client'

import { use, useState } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'

// Country data with flags and names
const countries: Record<string, { name: string; flag: string; region: string }> = {
  'us': { name: 'United States', flag: '🇺🇸', region: 'Americas' },
  'uk': { name: 'United Kingdom', flag: '🇬🇧', region: 'Europe' },
  'fr': { name: 'France', flag: '🇫🇷', region: 'Europe' },
  'de': { name: 'Germany', flag: '🇩🇪', region: 'Europe' },
  'it': { name: 'Italy', flag: '🇮🇹', region: 'Europe' },
  'es': { name: 'Spain', flag: '🇪🇸', region: 'Europe' },
  'jp': { name: 'Japan', flag: '🇯🇵', region: 'Asia-Pacific' },
  'cn': { name: 'China', flag: '🇨🇳', region: 'Asia-Pacific' },
  'ch': { name: 'Switzerland', flag: '🇨🇭', region: 'Europe' },
  'nl': { name: 'Netherlands', flag: '🇳🇱', region: 'Europe' },
  'au': { name: 'Australia', flag: '🇦🇺', region: 'Asia-Pacific' },
  'br': { name: 'Brazil', flag: '🇧🇷', region: 'Americas' },
  'mx': { name: 'Mexico', flag: '🇲🇽', region: 'Americas' },
  'gh': { name: 'Ghana', flag: '🇬🇭', region: 'Africa' },
  'ng': { name: 'Nigeria', flag: '🇳🇬', region: 'Africa' },
  'za': { name: 'South Africa', flag: '🇿🇦', region: 'Africa' },
  'ae': { name: 'United Arab Emirates', flag: '🇦🇪', region: 'Middle East' },
}

// Sample data for countries
const countryData: Record<string, {
  volume: string
  lots: string
  change: number
  avgHammer: string
  artistCount: string
  description: string
}> = {
  'us': {
    volume: '$4.2B',
    lots: '186K',
    change: 8.4,
    avgHammer: '$23K',
    artistCount: '42K',
    description: 'The world\'s largest art market, home to Abstract Expressionism, Pop Art, and leading contemporary artists.',
  },
  'uk': {
    volume: '$2.8B',
    lots: '124K',
    change: 5.2,
    avgHammer: '$22K',
    artistCount: '28K',
    description: 'Major art hub with strong auction presence, from Old Masters to YBAs and contemporary.',
  },
  'fr': {
    volume: '$1.8B',
    lots: '98K',
    change: 3.8,
    avgHammer: '$18K',
    artistCount: '24K',
    description: 'Birthplace of Impressionism, with deep history in modern and contemporary art.',
  },
  'de': {
    volume: '$980M',
    lots: '67K',
    change: 6.2,
    avgHammer: '$15K',
    artistCount: '18K',
    description: 'Strong tradition in Expressionism, Bauhaus, and contemporary conceptual art.',
  },
  'jp': {
    volume: '$1.2B',
    lots: '45K',
    change: 14.8,
    avgHammer: '$27K',
    artistCount: '12K',
    description: 'Dynamic market spanning traditional to contemporary, including Gutai, Mono-ha, and Superflat.',
  },
  'cn': {
    volume: '$2.4B',
    lots: '89K',
    change: 12.4,
    avgHammer: '$27K',
    artistCount: '15K',
    description: 'Rapidly growing market with strong demand for contemporary Chinese art and traditional works.',
  },
  'gh': {
    volume: '$120M',
    lots: '2K',
    change: 42.5,
    avgHammer: '$60K',
    artistCount: '450',
    description: 'Emerging market leading the African contemporary art scene with global recognition.',
  },
}

const topArtistsByCountry: Record<string, { name: string; slug: string; volume: string; change: number; tier: string }[]> = {
  'us': [
    { name: 'Jean-Michel Basquiat', slug: 'jean-michel-basquiat', volume: '$285M', change: 12.1, tier: 'Blue-chip' },
    { name: 'Andy Warhol', slug: 'andy-warhol', volume: '$248M', change: 6.8, tier: 'Blue-chip' },
    { name: 'Mark Rothko', slug: 'mark-rothko', volume: '$186M', change: 8.4, tier: 'Blue-chip' },
    { name: 'Cy Twombly', slug: 'cy-twombly', volume: '$142M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Ed Ruscha', slug: 'ed-ruscha', volume: '$98M', change: 8.5, tier: 'Blue-chip' },
  ],
  'uk': [
    { name: 'David Hockney', slug: 'david-hockney', volume: '$186M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Banksy', slug: 'banksy', volume: '$98M', change: 15.2, tier: 'Blue-chip' },
    { name: 'Francis Bacon', slug: 'francis-bacon', volume: '$156M', change: 2.8, tier: 'Blue-chip' },
    { name: 'Damien Hirst', slug: 'damien-hirst', volume: '$72M', change: -8.4, tier: 'Blue-chip' },
    { name: 'Lucian Freud', slug: 'lucian-freud', volume: '$68M', change: 5.1, tier: 'Blue-chip' },
  ],
  'jp': [
    { name: 'Yayoi Kusama', slug: 'yayoi-kusama', volume: '$180M', change: 18.4, tier: 'Blue-chip' },
    { name: 'Yoshitomo Nara', slug: 'yoshitomo-nara', volume: '$72M', change: 24.7, tier: 'Blue-chip' },
    { name: 'Takashi Murakami', slug: 'takashi-murakami', volume: '$56M', change: 8.2, tier: 'Blue-chip' },
    { name: 'Kazuo Shiraga', slug: 'kazuo-shiraga', volume: '$42M', change: 12.4, tier: 'Blue-chip' },
    { name: 'Tsuguharu Foujita', slug: 'tsuguharu-foujita', volume: '$38M', change: 6.8, tier: 'Blue-chip' },
  ],
  'de': [
    { name: 'Gerhard Richter', slug: 'gerhard-richter', volume: '$156M', change: -2.3, tier: 'Blue-chip' },
    { name: 'Georg Baselitz', slug: 'georg-baselitz', volume: '$68M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Anselm Kiefer', slug: 'anselm-kiefer', volume: '$56M', change: 8.5, tier: 'Blue-chip' },
    { name: 'Sigmar Polke', slug: 'sigmar-polke', volume: '$45M', change: 6.2, tier: 'Blue-chip' },
    { name: 'Joseph Beuys', slug: 'joseph-beuys', volume: '$32M', change: 2.8, tier: 'Blue-chip' },
  ],
  'gh': [
    { name: 'Amoako Boafo', slug: 'amoako-boafo', volume: '$45M', change: 42.1, tier: 'Mid-market' },
    { name: 'El Anatsui', slug: 'el-anatsui', volume: '$28M', change: 18.4, tier: 'Blue-chip' },
    { name: 'Otis Kwame Kye Quaicoe', slug: 'otis-kwame-kye-quaicoe', volume: '$12M', change: 65.2, tier: 'Mid-market' },
    { name: 'Serge Attukwei Clottey', slug: 'serge-attukwei-clottey', volume: '$4M', change: 38.5, tier: 'Emerging' },
    { name: 'Zohra Opoku', slug: 'zohra-opoku', volume: '$2M', change: 52.1, tier: 'Emerging' },
  ],
}

const periodBreakdown = [
  { period: 'Contemporary', volume: '$4.2B', share: 48, change: 12.4 },
  { period: 'Modern', volume: '$2.4B', share: 28, change: 4.2 },
  { period: 'Post-War', volume: '$1.4B', share: 16, change: 6.8 },
  { period: 'Old Masters', volume: '$0.7B', share: 8, change: 2.1 },
]

function ShareButton({ title }: { title?: string }) {
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const shareUrl = window.location.href
    const shareTitle = title || 'Country - salle.art'

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl })
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleShare}
      className="pill text-sm flex items-center gap-2"
      style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </>
      )}
    </button>
  )
}

export default function CountryPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params)
  const { canExport } = useAuth()

  // Find country info
  const countryInfo = countries[id.toLowerCase()]
  const countryName = countryInfo?.name || id.toUpperCase()
  const flag = countryInfo?.flag || '🌍'
  const region = countryInfo?.region || 'Global'

  const data = countryData[id.toLowerCase()] || {
    volume: '$200M',
    lots: '5K',
    change: 5.0,
    avgHammer: '$40K',
    artistCount: '1K',
    description: `Art market data for ${countryName}.`,
  }

  const topArtists = topArtistsByCountry[id.toLowerCase()] || topArtistsByCountry['us'] || []

  // AI outlook
  const aiOutlook = {
    summary: `The ${countryName} art market shows ${data.change >= 0 ? 'positive' : 'negative'} momentum with ${Math.abs(data.change)}% ${data.change >= 0 ? 'growth' : 'decline'} YoY. ${data.change >= 15 ? 'Exceptional growth driven by international collector interest and institutional recognition.' : data.change >= 5 ? 'Healthy market activity with consistent demand across tiers.' : data.change >= 0 ? 'Stable market with selective buying.' : 'Market cooling presents buying opportunities.'}`,
    recommendation: data.change >= 0
      ? `Focus on artists with strong ${countryName} institutional support and international exhibition history.`
      : `Prioritize established artists with proven auction records and museum representation.`,
  }

  return (
    <div className="px-4 md:px-6 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-6">
        <Link href="/app/market" className="hover:text-text-secondary transition-colors">
          Market
        </Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-secondary">Country</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-primary">{countryName}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-4xl">{flag}</span>
              <h1 className="font-display text-3xl md:text-4xl font-light">{countryName}</h1>
            </div>
            <p className="text-text-secondary max-w-xl mb-2">{data.description}</p>
            <p className="text-sm text-text-tertiary">
              Region: <span className="text-text-secondary">{region}</span> · {data.artistCount} artists tracked
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <ShareButton title={`${countryName} Art Market - salle.art`} />
            <button
              className="pill text-sm flex items-center gap-2"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Set Alert
            </button>
            <TierGate feature="export" showPrompt={false}>
              <button
                className="pill text-sm flex items-center gap-2"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            </TierGate>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Total Volume</p>
          <p className="font-display text-2xl font-light">{data.volume}</p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Total Lots</p>
          <p className="font-display text-2xl font-light">{data.lots}</p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">YoY Change</p>
          <p
            className="font-display text-2xl font-light"
            style={{ color: data.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}
          >
            {data.change >= 0 ? '+' : ''}{data.change}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Avg. Hammer</p>
          <p className="font-display text-2xl font-light">{data.avgHammer}</p>
        </div>
      </div>

      {/* AI Outlook */}
      <div className="card p-5 mb-8 border-l-4" style={{ borderLeftColor: 'var(--accent-primary)' }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium mb-2">AI Market Outlook</h3>
            <p className="text-sm text-text-secondary mb-3">{aiOutlook.summary}</p>
            <div className="bg-page rounded-lg p-3">
              <p className="text-xs text-text-tertiary mb-1">Recommendation</p>
              <p className="text-sm text-text-primary">{aiOutlook.recommendation}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Period breakdown */}
      <div className="card p-6 mb-8">
        <h2 className="font-medium mb-4">By Period</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {periodBreakdown.map((period) => (
            <Link
              key={period.period}
              href={`/app/market/${period.period.toLowerCase()}`}
              className="bg-page rounded-xl p-4 hover:bg-card transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{period.period}</span>
                <span className={`text-xs tabular-nums ${period.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {period.change >= 0 ? '+' : ''}{period.change}%
                </span>
              </div>
              <p className="text-lg font-display font-light mb-1">{period.volume}</p>
              <div className="w-full bg-card rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${period.share}%`, background: 'var(--accent-primary)' }}
                />
              </div>
              <p className="text-xs text-text-tertiary mt-1">{period.share}% share</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Top artists */}
      <div className="card p-6">
        <h2 className="font-medium mb-4">Top {countryName} Artists</h2>
        <div className="space-y-3">
          {topArtists.map((artist, i) => (
            <Link
              key={artist.slug}
              href={`/app/artists/${artist.slug}`}
              className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0 hover:bg-page -mx-3 px-3 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-text-tertiary text-sm w-6">{i + 1}</span>
                <div>
                  <p className="font-medium text-sm">{artist.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    artist.tier === 'Blue-chip'
                      ? 'bg-accent-primary/10 text-accent-primary'
                      : artist.tier === 'Mid-market'
                      ? 'bg-accent-warm/10 text-accent-warm'
                      : 'bg-positive/10 text-positive'
                  }`}>
                    {artist.tier}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-sm tabular-nums">{artist.volume}</p>
                <p className={`text-xs tabular-nums ${artist.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {artist.change >= 0 ? '+' : ''}{artist.change}%
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
